/**
 * Yugen Home Page Hero
 * Manual full-viewport slider. No autoplay.
 * Arrows, pagination counter, theme-editor block select, mobile swipe.
 * Optional per-slide Day / Night toggle with lights-off crossfade.
 */
(function () {
  'use strict';

  var SWIPE_THRESHOLD = 45;

  if (customElements.get('yugen-home-page-hero')) return;

  class YugenHomePageHero extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onDayNightToggle = this.onDayNightToggle.bind(this);
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-yhh-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-yhh-dot]'));
      this.prevBtn = this.querySelector('[data-yhh-prev]');
      this.nextBtn = this.querySelector('[data-yhh-next]');
      this.stage = this.querySelector('[data-yhh-stage]');
      this.currentEl = this.querySelector('[data-yhh-current]');
      this.totalEl = this.querySelector('[data-yhh-total]');
      this.dnToggles = Array.from(this.querySelectorAll('[data-yhh-dn-toggle]'));

      if (this.slides.length === 0) return;

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
      this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
      this.dnToggles.forEach((btn) => btn.addEventListener('click', this.onDayNightToggle));
      this.addEventListener('keydown', this.onKeydown);

      if (this.stage) {
        this.stage.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.stage.addEventListener('touchmove', this.onTouchMove, { passive: true });
        this.stage.addEventListener('touchend', this.onTouchEnd, { passive: true });
      }

      this.addEventListener('shopify:block:select', this.onBlockSelect);

      if (this.totalEl) {
        this.totalEl.textContent = String(this.slides.length).padStart(2, '0');
      }

      this.setActive(0, { instant: true });
      this.setAttribute('tabindex', '0');
    }

    disconnectedCallback() {
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
      if (this.dots) this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
      if (this.dnToggles) {
        this.dnToggles.forEach((btn) => btn.removeEventListener('click', this.onDayNightToggle));
      }
      this.removeEventListener('keydown', this.onKeydown);
      if (this.stage) {
        this.stage.removeEventListener('touchstart', this.onTouchStart);
        this.stage.removeEventListener('touchmove', this.onTouchMove);
        this.stage.removeEventListener('touchend', this.onTouchEnd);
      }
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    onPrev() {
      this.setActive(this.index - 1);
    }

    onNext() {
      this.setActive(this.index + 1);
    }

    onDotClick(event) {
      var i = parseInt(event.currentTarget.getAttribute('data-yhh-dot'), 10);
      if (!isNaN(i)) this.setActive(i);
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.onPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.onNext();
      }
    }

    onBlockSelect(event) {
      var slide = event.target.closest('[data-yhh-slide]');
      var i = slide ? this.slides.indexOf(slide) : -1;
      if (i !== -1) this.setActive(i, { instant: true });
    }

    onTouchStart(event) {
      var t = event.changedTouches[0];
      this.touchStartX = t.clientX;
      this.touchStartY = t.clientY;
      this.touchDX = 0;
      this.touchDY = 0;
    }

    onTouchMove(event) {
      if (this.touchStartX == null) return;
      var t = event.changedTouches[0];
      this.touchDX = t.clientX - this.touchStartX;
      this.touchDY = t.clientY - this.touchStartY;
    }

    onTouchEnd() {
      if (this.touchStartX == null) return;
      var dx = this.touchDX || 0;
      var dy = this.touchDY || 0;
      if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.4) {
        this.setActive(this.index + (dx < 0 ? 1 : -1));
      }
      this.touchStartX = null;
      this.touchStartY = null;
    }

    onDayNightToggle(event) {
      var slide = event.currentTarget.closest('[data-yhh-slide]');
      if (!slide || !slide.classList.contains('yhh__slide--day-night')) return;
      if (slide.classList.contains('is-dn-busy')) return;

      var mode = slide.getAttribute('data-yhh-mode') || 'day';
      this.setSlideMode(slide, mode === 'day' ? 'night' : 'day');
    }

    wait(ms) {
      return new Promise(function (resolve) {
        window.setTimeout(resolve, ms);
      });
    }

    async setSlideMode(slide, next) {
      if (!slide || slide.classList.contains('is-dn-busy')) return;
      var current = slide.getAttribute('data-yhh-mode') || 'day';
      if (next === current) return;

      var dayScene = slide.querySelector('[data-yhh-scene="day"]');
      var nightScene = slide.querySelector('[data-yhh-scene="night"]');
      var lights = slide.querySelector('[data-yhh-lights]');
      var toggle = slide.querySelector('[data-yhh-dn-toggle]');
      if (!dayScene || !nightScene) return;

      slide.classList.add('is-dn-busy');
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var goingNight = next === 'night';

      if (goingNight) slide.classList.add('is-lights-out');
      else slide.classList.remove('is-lights-out');

      if (!reduce && lights) {
        lights.classList.add('is-on');
        await this.wait(goingNight ? 420 : 320);
      }

      slide.setAttribute('data-yhh-mode', next);
      slide.classList.toggle('is-day', next === 'day');
      slide.classList.toggle('is-night', next === 'night');

      dayScene.classList.toggle('is-active', next === 'day');
      nightScene.classList.toggle('is-active', next === 'night');
      dayScene.setAttribute('aria-hidden', next === 'day' ? 'false' : 'true');
      nightScene.setAttribute('aria-hidden', next === 'night' ? 'false' : 'true');

      if (toggle) {
        toggle.setAttribute('aria-pressed', next === 'night' ? 'true' : 'false');
        toggle.setAttribute('aria-label', next === 'night' ? 'Switch to day' : 'Switch to night');
      }

      if (!reduce && lights) {
        await this.wait(80);
        lights.classList.remove('is-on');
        await this.wait(goingNight ? 560 : 480);
      }

      slide.classList.remove('is-dn-busy');
    }

    setActive(next, options) {
      var max = this.slides.length - 1;
      var i = Math.max(0, Math.min(next, max));
      var instant = options && options.instant;
      this.index = i;

      this.classList.toggle('is-instant', !!instant);

      this.slides.forEach(function (slide, idx) {
        var active = idx === i;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      this.dots.forEach(function (dot, idx) {
        var active = idx === i;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (this.currentEl) {
        this.currentEl.textContent = String(i + 1).padStart(2, '0');
      }

      if (this.prevBtn) this.prevBtn.disabled = i === 0;
      if (this.nextBtn) this.nextBtn.disabled = i === max;

      if (instant) {
        requestAnimationFrame(
          function () {
            this.classList.remove('is-instant');
          }.bind(this)
        );
      }
    }
  }

  customElements.define('yugen-home-page-hero', YugenHomePageHero);
})();
